/**
 * Property Utilities for FreshCards
 * Helper functions for working with database properties
 */

import { 
  PropertyDefinition, 
  PropertyType, 
  PropertyColor,
  PROPERTY_COLORS,
  SelectOption
} from '../types/database/property';

// Get a random property color
export function getRandomPropertyColor(): PropertyColor {
  const colors = Object.keys(PROPERTY_COLORS) as PropertyColor[];
  return colors[Math.floor(Math.random() * colors.length)];
}

// Get color for a select/multi-select option
export function getPropertyColor(colorName?: string): PropertyColor {
  if (!colorName) return 'gray';
  if (colorName in PROPERTY_COLORS) {
    return colorName as PropertyColor;
  }
  return 'gray';
}

// Create a default property definition
export function createPropertyDefinition(
  name: string,
  type: PropertyType,
  options?: Partial<PropertyDefinition>
): PropertyDefinition {
  return {
    id: `prop_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    type,
    visible: true,
    position: 0,
    ...options,
  };
}

// Default select options
export const DEFAULT_STATUS_OPTIONS: SelectOption[] = [
  { id: 'status_1', name: 'To Do', color: 'gray' },
  { id: 'status_2', name: 'In Progress', color: 'blue' },
  { id: 'status_3', name: 'Done', color: 'green' },
];

// Default priority options
export const DEFAULT_PRIORITY_OPTIONS: SelectOption[] = [
  { id: 'prio_1', name: 'Urgent', color: 'red' },
  { id: 'prio_2', name: 'High', color: 'orange' },
  { id: 'prio_3', name: 'Medium', color: 'yellow' },
  { id: 'prio_4', name: 'Low', color: 'gray' },
];

// Format number based on format type
export function formatNumber(value: number, format?: string): string {
  if (format === 'dollar') {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }
  if (format === 'percent') {
    return `${value.toFixed(2)}%`;
  }
  if (format === 'comma') {
    return value.toLocaleString();
  }
  return value.toString();
}

// Format date based on format string
export function formatDate(date: Date, format?: string): string {
  if (format === 'relative') {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  }
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Get icon for property type
export function getPropertyTypeIcon(type: PropertyType): string {
  const icons: Record<PropertyType, string> = {
    title: '📝',
    text: '📄',
    number: '#️⃣',
    select: '🏷️',
    multi_select: '🏷️',
    date: '📅',
    checkbox: '☑️',
    url: '🔗',
    files: '📎',
    relation: '🔗',
    formula: '🔢',
    status: '⚡',
    person: '👤',
    phone: '📞',
    email: '✉️',
    created_time: '🕐',
    last_edited_time: '🕐',
  };
  return icons[type] || '📋';
}

// Validate property value based on type
export function validatePropertyValue(
  value: unknown,
  type: PropertyType,
  definition: PropertyDefinition
): { valid: boolean; error?: string } {
  switch (type) {
    case 'number':
      if (typeof value !== 'number' && !isNaN(Number(value))) {
        return { valid: false, error: 'Must be a valid number' };
      }
      break;

    case 'checkbox':
      if (typeof value !== 'boolean') {
        return { valid: false, error: 'Must be true or false' };
      }
      break;
    
    case 'select':
      if (definition.options && !definition.options.find(o => o.name === value)) {
        return { valid: false, error: 'Must be one of the available options' };
      }
      break;
    
    case 'multi_select':
      if (!Array.isArray(value)) {
        return { valid: false, error: 'Must be an array of selections' };
      }
      break;
    
    case 'url':
      if (typeof value === 'string' && value) {
        try {
          new URL(value);
        } catch {
          return { valid: false, error: 'Must be a valid URL' };
        }
      }
      break;
    
    case 'email':
      if (typeof value === 'string' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return { valid: false, error: 'Must be a valid email address' };
        }
      }
      break;
  }
  
  return { valid: true };
}
